import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toast';
import {
  LuGlobe, LuLock, LuRadar, LuInfo, LuLayers, LuClock, LuLoader,
  LuMousePointerClick, LuFileText, LuClipboardList
} from 'react-icons/lu';
import axiosInstance from '../../../api/axiosInstance';
import { useStartUIDiscovery } from '../../../hooks/API/wizard.api';
import { getApiErrorMessage } from '../../../utils/apiError';
import ExecutionModeModal, { EXECUTION_MODES } from '../../wizard/steps/discovery/ExecutionModeModal';

const LOCAL_AGENT_START_TIMEOUT_MS = 30000;

const inputClass = (hasError) =>
  `w-full rounded-lg border bg-[var(--input-background)] px-3.5 py-2.5 text-sm text-[color:var(--text-primary)] placeholder-[color:var(--input-placeholder)] shadow-sm outline-none transition focus:border-[var(--primary)] focus:[box-shadow:var(--focus-ring)] disabled:cursor-not-allowed disabled:bg-[var(--surface-disabled)] ${
    hasError ? 'border-[var(--danger)]' : 'border-[var(--input-border)]'
  }`;

const FieldError = ({ message }) =>
  message ? <p className="mt-1.5 text-xs font-medium text-[color:var(--danger)]">{message}</p> : null;

const DISCOVERY_TARGETS = [
  { icon: LuFileText, label: 'Pages & routes' },
  { icon: LuMousePointerClick, label: 'Buttons & links' },
  { icon: LuClipboardList, label: 'Forms & inputs' },
  { icon: LuLayers, label: 'Stable selectors' }
];

/**
 * Crawl setup form for the in-app "Crawl Project" page. Mirrors the wizard's
 * Step6CDiscoverySetup behaviour (validation, /crawler/discovery/start payload,
 * local agent handshake) without depending on the onboarding context.
 */
const CrawlSetupPanel = ({ projectId, onStarted, onCancel }) => {
  const { mutateAsync: startDiscovery } = useStartUIDiscovery();
  const [formData, setFormData] = useState({
    baseUrl: '',
    maxDepth: 3,
    maxPages: 100,
    requireAuth: false,
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isStarting, setIsStarting] = useState(false);
  const [showExecutionModeModal, setShowExecutionModeModal] = useState(false);
  const isMountedRef = useRef(true);
  const agentListenerCleanupRef = useRef(null);
  const baseUrlTouchedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      agentListenerCleanupRef.current?.();
    };
  }, []);

  // Prefill the base URL from the project's first environment, unless the user already typed one
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    axiosInstance
      .get(`/user_profile/list_environments?project_id=${projectId}`, { skipGlobalToast: true, skipGlobalLoader: true })
      .then((res) => {
        if (cancelled || baseUrlTouchedRef.current) return;
        const envs = Array.isArray(res?.data) ? res.data : [];
        const env = envs.find((e) => String(e?.status).toLowerCase() === 'active' && e?.target_url)
          || envs.find((e) => e?.target_url);
        if (env?.target_url) {
          setFormData((prev) => (prev.baseUrl ? prev : { ...prev, baseUrl: env.target_url }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'baseUrl') baseUrlTouchedRef.current = true;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Base URL is required';
    } else {
      try {
        new URL(formData.baseUrl);
      } catch (e) {
        newErrors.baseUrl = 'Please enter a valid URL';
      }
    }

    if (formData.requireAuth) {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required for authenticated discovery';
      }
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required for authenticated discovery';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Resolves true on DISCOVERY_START_SUCCESS for this run, false on timeout
  const waitForLocalAgentStart = (runId) =>
    new Promise((resolve) => {
      agentListenerCleanupRef.current?.();

      const handleMessage = (event) => {
        if (event.source !== window) return;
        const data = event.data;
        if (
          data?.source === 'ROBITEST_AGENT' &&
          data.type === 'DISCOVERY_START_SUCCESS' &&
          data.runId === runId
        ) {
          cleanup();
          resolve(true);
        }
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(false);
      }, LOCAL_AGENT_START_TIMEOUT_MS);

      const cleanup = () => {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handleMessage);
        agentListenerCleanupRef.current = null;
      };

      agentListenerCleanupRef.current = cleanup;
      window.addEventListener('message', handleMessage);
    });

  const handleOpenExecutionModeModal = (e) => {
    e?.preventDefault();
    if (!validate()) return;

    if (!projectId) {
      toast.error('Project is required to start discovery');
      return;
    }

    setShowExecutionModeModal(true);
  };

  const handleStartDiscovery = async (executionMode) => {
    setIsStarting(true);

    try {
      const payload = {
        base_url: formData.baseUrl.trim(),
        max_depth: Number(formData.maxDepth),
        max_pages: Number(formData.maxPages),
        requires_authentication: Boolean(formData.requireAuth),
        execution_mode: executionMode
      };

      if (payload.requires_authentication) {
        payload.username = formData.username.trim();
        payload.password = formData.password;
      }

      const response = await startDiscovery({ projectId, payload });

      if (response?.status_code === 200 && response.run_id) {
        if (executionMode === EXECUTION_MODES.LOCAL) {
          // Listener must be attached before posting so a fast agent reply isn't missed
          const agentStarted = waitForLocalAgentStart(response.run_id);
          window.postMessage({
            source: 'ROBITEST_CLOUD',
            action: 'TRIGGER_UI_DISCOVERY',
            runId: response.run_id,
            projectId,
            baseUrl: payload.base_url,
            maxDepth: payload.max_depth,
            maxPages: payload.max_pages,
            authenticationRequired: payload.requires_authentication,
            ...(payload.requires_authentication && {
              username: payload.username,
              password: payload.password
            }),

          }, '*');

          const started = await agentStarted;
          if (!isMountedRef.current) return;
          if (!started) {
            toast.error('Local agent did not respond. Make sure it is installed and running, then try again.');
            return;
          }
        }

        setShowExecutionModeModal(false);
        onStarted({
          runId: response.run_id,
          baseUrl: payload.base_url,
          executionMode
        });
      } else {
        toast.error(response?.message || 'Failed to start UI discovery');
      }
    } catch (error) {
      console.error('Error starting discovery:', error);
      toast.error(getApiErrorMessage(error, 'Failed to start discovery. Please try again.'));
    } finally {
      if (isMountedRef.current) setIsStarting(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Configuration form */}
        <form
          onSubmit={handleOpenExecutionModeModal}
          noValidate
          className="flex flex-col rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-sm lg:col-span-2"
        >
          <div className="border-b border-[var(--border-subtle)] px-5 py-4 sm:px-6">
            <h2 className="text-base font-semibold text-[color:var(--text-primary)]">Discovery configuration</h2>
            <p className="mt-0.5 text-sm text-[color:var(--text-muted)]">Tell the crawler where to start and how far to go.</p>
          </div>

          <div className="flex-1 space-y-5 px-5 py-5 sm:px-6">
            <div>
              <label htmlFor="crawl-baseUrl" className="mb-1.5 flex items-center gap-2 text-sm font-medium text-[color:var(--text-secondary)]">
                <LuGlobe size={16} className="text-[color:var(--text-subtle)]" />
                Base URL <span className="text-[color:var(--danger)]">*</span>
              </label>
              <input
                id="crawl-baseUrl"
                type="url"
                name="baseUrl"
                className={inputClass(errors.baseUrl)}
                value={formData.baseUrl}
                onChange={handleChange}
                placeholder="https://your-application.com"
                disabled={isStarting}
                autoComplete="url"
              />
              {errors.baseUrl ? (
                <FieldError message={errors.baseUrl} />
              ) : (
                <p className="mt-1.5 text-xs text-[color:var(--text-muted)]">Starting point for the crawler</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="crawl-maxDepth" className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">
                  Maximum depth
                </label>
                <select
                  id="crawl-maxDepth"
                  name="maxDepth"
                  className={inputClass(false)}
                  value={formData.maxDepth}
                  onChange={handleChange}
                  disabled={isStarting}
                >
                  <option value={1}>1 level (shallow)</option>
                  <option value={2}>2 levels</option>
                  <option value={3}>3 levels (recommended)</option>
                  <option value={4}>4 levels</option>
                  <option value={5}>5 levels (deep)</option>
                </select>
                <p className="mt-1.5 text-xs text-[color:var(--text-muted)]">How many clicks deep to explore</p>
              </div>

              <div>
                <label htmlFor="crawl-maxPages" className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">
                  Maximum pages
                </label>
                <select
                  id="crawl-maxPages"
                  name="maxPages"
                  className={inputClass(false)}
                  value={formData.maxPages}
                  onChange={handleChange}
                  disabled={isStarting}
                >
                  <option value={50}>50 pages</option>
                  <option value={100}>100 pages (recommended)</option>
                  <option value={250}>250 pages</option>
                  <option value={500}>500 pages</option>
                </select>
                <p className="mt-1.5 text-xs text-[color:var(--text-muted)]">Limit to prevent excessive crawling</p>
              </div>
            </div>

            {/* Authentication */}
            <div
              className={`rounded-xl border p-4 transition-colors ${
                formData.requireAuth ? 'border-[var(--primary)] bg-[var(--primary-subtle)]' : 'border-[var(--border)] bg-[var(--bg-tertiary)]'
              }`}
            >
              <label htmlFor="crawl-requireAuth" className="flex cursor-pointer items-start gap-3">
                <input
                  id="crawl-requireAuth"
                  type="checkbox"
                  name="requireAuth"
                  checked={formData.requireAuth}
                  onChange={handleChange}
                  disabled={isStarting}
                  className="mt-0.5 h-4 w-4 cursor-pointer rounded border-[var(--border-strong)] accent-[var(--primary)]"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-2 text-sm font-medium text-[color:var(--text-primary)]">
                    <LuLock size={15} className="text-[color:var(--text-muted)]" />
                    Application requires authentication
                  </span>
                  <span className="mt-0.5 block text-xs text-[color:var(--text-muted)]">
                    The crawler signs in first so it can reach pages behind the login.
                  </span>
                </span>
              </label>

              {formData.requireAuth && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="crawl-username" className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">
                      Username <span className="text-[color:var(--danger)]">*</span>
                    </label>
                    <input
                      id="crawl-username"
                      type="text"
                      name="username"
                      className={inputClass(errors.username)}
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="test@example.com"
                      disabled={isStarting}
                      autoComplete="off"
                    />
                    <FieldError message={errors.username} />
                  </div>
                  <div>
                    <label htmlFor="crawl-password" className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">
                      Password <span className="text-[color:var(--danger)]">*</span>
                    </label>
                    <input
                      id="crawl-password"
                      type="password"
                      name="password"
                      className={inputClass(errors.password)}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      disabled={isStarting}
                      autoComplete="new-password"
                    />
                    <FieldError message={errors.password} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--border-subtle)] bg-[var(--bg-tertiary)] px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6 rounded-b-2xl">
            <button
              type="button"
              onClick={onCancel}
              disabled={isStarting}
              className="w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface)] px-5 py-2.5 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isStarting || !formData.baseUrl.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-110 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {isStarting ? <LuLoader size={18} className="animate-spin" /> : <LuRadar size={18} />}
              {isStarting ? 'Starting...' : 'Start Discovery'}
            </button>
          </div>
        </form>

        {/* Side info */}
        <aside className="flex flex-col gap-5">
          <div className="rounded-2xl border border-[var(--border)] bg-gradient-to-br from-[var(--primary-muted)] to-[var(--surface)] p-5 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--text-primary)]">
              <LuInfo size={18} className="text-[var(--primary-color)]" />
              What is UI Discovery?
            </div>
            <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-secondary)]">
              Our crawler automatically explores your application, discovering pages, forms, buttons and
              other UI elements. This builds a UI Map that enables reliable test automation without manual recording.
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-2">
              {DISCOVERY_TARGETS.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-2 rounded-lg bg-[var(--surface)] px-2.5 py-2 text-xs font-medium text-[color:var(--text-secondary)] ring-1 ring-[var(--border-subtle)]"
                >
                  <Icon size={14} className="shrink-0 text-[var(--primary-color)]" />
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--warning-muted)] text-[color:var(--warning)]">
              <LuClock size={22} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-primary)]">Estimated time: 3–10 minutes</div>
              <div className="mt-0.5 text-xs text-[color:var(--text-muted)]">Larger applications may take longer</div>
            </div>
          </div>
        </aside>
      </div>

      <ExecutionModeModal
        isOpen={showExecutionModeModal}
        onClose={() => setShowExecutionModeModal(false)}
        onConfirm={handleStartDiscovery}
        isLoading={isStarting}
      />
    </>
  );
};

export default CrawlSetupPanel;
